import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Loader2, ExternalLink, Briefcase } from "lucide-react";
import api from "../lib/api";

// Format Finviz field values for display
const isPositive = (v) => v && v.startsWith && (!v.startsWith("-")) && /\d/.test(v);
const isNegative = (v) => v && v.startsWith && v.startsWith("-");

const SECTIONS = [
  {
    title: "Valuation",
    fields: ["Market Cap", "P/E", "Forward P/E", "PEG", "P/S", "P/B"],
  },
  {
    title: "Earnings & Dividend",
    fields: ["EPS (ttm)", "EPS next Y", "EPS next 5Y", "Dividend TTM", "Dividend Est.", "Earnings"],
  },
  {
    title: "Risk & Volatility",
    fields: ["Beta", "ATR", "Volatility (Week)", "Volatility (Month)", "52W High", "52W Low"],
  },
  {
    title: "Performance",
    fields: ["Perf Week", "Perf Month", "Perf Quarter", "Perf YTD", "Perf Year"],
  },
  {
    title: "Profitability",
    fields: ["ROE", "ROA", "ROI", "Profit Margin", "Gross Margin", "Debt/Eq"],
  },
  {
    title: "Ownership",
    fields: ["Shs Outstand", "Shs Float", "Inst Own", "Insider Own", "Short Float", "Short Ratio"],
  },
];

export default function FundamentalsCard({ symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(false);
    api.get(`/fundamentals/${symbol}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setError(true); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [symbol]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-center gap-2 text-sm text-[#6B7068]" data-testid="fundamentals-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading fundamentals...
      </div>
    );
  }
  if (error || !data) {
    return null;  // Silently hide if Finviz is unavailable for this ticker
  }

  const f = data.fundamentals || {};
  const sector = f["Sector"];
  const industry = f["Industry"];
  const recom = f["Recom"];

  return (
    <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6" data-testid="fundamentals-card">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-1">
            Fundamentals
          </div>
          <div className="font-heading text-xl tracking-tight text-[#2A2F2B]">{data.company}</div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {sector && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#547A8F] bg-[#547A8F]/10 rounded-full px-2 py-0.5">
                <Briefcase className="w-3 h-3" strokeWidth={1.75} />
                {sector}
              </span>
            )}
            {industry && (
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B7068]">
                · {industry}
              </span>
            )}
          </div>
        </div>
        {data.website && (
          <a
            href={data.website}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fundamentals-website"
            className="text-xs text-[#547A8F] hover:text-[#426071] flex items-center gap-1"
          >
            Site <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </a>
        )}
      </div>

      {recom && (
        <div className="mb-5 p-4 rounded-xl bg-[#F9F8F6] border border-[#EAE6E1]">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B7068]">
                Analyst recommendation
              </div>
              <div className="text-xs text-[#6B7068] mt-0.5">
                Scale: 1 (Strong Buy) → 5 (Strong Sell)
              </div>
            </div>
            <div className="font-mono-ticker text-2xl text-[#2A2F2B]">{recom}</div>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {SECTIONS.map((sec) => {
          const visible = sec.fields.filter((field) => f[field]);
          if (!visible.length) return null;
          return (
            <div key={sec.title} data-testid={`fundamentals-section-${sec.title.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-2">
                {sec.title}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                {visible.map((field) => {
                  const val = f[field];
                  let color = "#2A2F2B";
                  if (sec.title === "Performance") {
                    if (isNegative(val)) color = "#C66B59";
                    else if (isPositive(val)) color = "#5C8D73";
                  }
                  const Icon = sec.title === "Performance"
                    ? (isPositive(val) ? TrendingUp : isNegative(val) ? TrendingDown : null)
                    : null;
                  return (
                    <div key={field} className="border-b border-[#EAE6E1] pb-1.5 last:border-0">
                      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7068]">
                        {field}
                      </div>
                      <div className="font-mono-ticker text-sm flex items-center gap-1" style={{ color }}>
                        {Icon && <Icon className="w-3 h-3" strokeWidth={2.25} />}
                        {val}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-[#6B7068] mt-5 pt-3 border-t border-[#EAE6E1]">
        Data via Finviz. Not financial advice.
      </p>
    </div>
  );
}
